/* eslint-disable react-doctor/prefer-tag-over-role -- <dialog> element has conflicting browser defaults (position, margin) that would break our portal-based positioning */
'use client';

import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { useFocusTrap } from '@/hooks/useFocusTrap';

interface Props {
  startupCount: number;
  onDismiss: () => void;
}

const STEPS = [
  {
    label: 'Welcome',
    title: 'Brecha: a map of the bootstrapped SaaS landscape',
    body: (count: number) =>
      `${count.toLocaleString()} startups, organized by what they have in common. Each dot is a startup. The bigger the dot, the higher the MRR in USD.`,
    cta: 'Next',
  },
  {
    label: 'The map',
    title: 'How clusters work',
    body: () =>
      'An AI model reads each startup\'s name and description and finds the ones that talk about similar things. Those land close together and share a color. Hover a dot to preview, click for full details.',
    cta: 'Next',
  },
  {
    label: 'Filters',
    title: 'Explore with filters',
    body: () =>
      'Use the panel on the left to filter by MRR, audience, country, and more. Toggle clusters on or off to focus on what interests you. On mobile, pinch to zoom.',
    cta: 'Explore the map',
  },
] as const;

export const OnboardingModal: React.FC<Props> = ({ startupCount, onDismiss }) => {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const nextBtnRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef);

  useEffect(() => {
    nextBtnRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onDismiss]);

  const handleNext = (): void => {
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      onDismiss();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-bg/75 z-[300]"
        style={{ backdropFilter: 'blur(2px)' }}
        aria-hidden="true"
        onClick={onDismiss}
      />

      {/* Modal — desktop: centered; mobile: bottom sheet */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label="Welcome to Brecha"
        style={{ backgroundColor: 'var(--bg-panel)' }}
        className="fixed z-[301] border border-border rounded-sm shadow-lg overflow-y-auto
                   bottom-0 left-0 right-0 max-h-[85dvh] rounded-tl-[14px] rounded-tr-[14px] rounded-bl-none rounded-br-none
                   desk:bottom-auto desk:left-1/2 desk:-translate-x-1/2 desk:top-1/2 desk:-translate-y-1/2
                   desk:w-[480px] desk:max-h-[90dvh] desk:rounded-sm"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          {/* Step indicators */}
          <div className="flex items-center gap-[6px]">
            {STEPS.map((s, i) => (
              <div
                key={s.label}
                className={`h-[3px] rounded-full transition-all duration-200 ${
                  i === step ? 'w-[20px] bg-accent' : i < step ? 'w-[12px] bg-border-mid' : 'w-[12px] bg-border'
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Skip intro"
            className="p-1 -mr-1 text-text-muted hover:text-text-secondary transition-colors"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 pb-5">
          <div className="text-[9px] font-semibold tracking-[0.12em] uppercase text-text-muted mb-[6px]">
            {current.label}
          </div>
          <h2 className="text-[15px] font-semibold text-text-primary leading-snug mb-3">
            {current.title}
          </h2>
          <p className="text-[13px] text-text-secondary leading-[1.6]">
            {current.body(startupCount)}
          </p>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex items-center justify-between">
          <button
            type="button"
            onClick={onDismiss}
            className="text-[11px] text-text-muted hover:text-text-secondary transition-colors tracking-[0.04em]"
          >
            Skip
          </button>
          <button
            ref={nextBtnRef}
            type="button"
            onClick={handleNext}
            className="text-[12px] font-medium px-4 py-[7px] rounded-sm border border-accent text-accent hover:bg-accent-dim transition-colors tracking-[0.04em]"
          >
            <span className="inline-flex items-center gap-[5px]">
              {current.cta}{step < STEPS.length - 1 && <ArrowRight size={12} aria-hidden="true" />}
            </span>
          </button>
        </div>
      </div>
    </>
  );
};
